/**
 * The provider calendar (Scheduling > Views > Provider calendar): a week of visits, one lane per provider, built from
 * eMerge visit plans. Read-only: nothing here plans, confirms, completes or cancels a visit. The week and the
 * provider filter ride in the URL, so a link and a refresh land where the person was.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { outcomeOf, shortDate, stopWords } from '../finance/financeApi';
import { closureWords } from '../provider/providerApi';
import { roundProgressWords } from '../provider/roundProgress';
import { getProviderCalendar, type CalendarVisit, type CalendarVisitState, type ProviderCalendarPage } from './providerCalendarApi';

export const CALENDAR_DENIED = 'The provider calendar is for scheduling managers.';
const STATE_WORD: Record<CalendarVisitState, string> = { PLANNED: 'Planned', CONFIRMED: 'Confirmed', COMPLETED: 'Completed' };
const NO_PROVIDER = 'No provider';

const dayMs = (iso: string) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso); return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN; };
const weekday = (iso: string) => new Date(dayMs(iso)).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short' });
const monthDay = (iso: string) => new Date(dayMs(iso)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
const isWeekend = (iso: string) => { const d = new Date(dayMs(iso)).getUTCDay(); return d === 0 || d === 6; };

/** "Today", "Tomorrow", "in 5 days" against the server's day. */
function relativeDay(iso: string, today: string): string {
  const n = Math.round((dayMs(iso) - dayMs(today)) / 86400000);
  if (Number.isNaN(n)) return '';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n === -1) return 'Yesterday';
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
}

function ClosedFlag({ visit }: { visit: CalendarVisit }) {
  const words = closureWords(visit.building_closed);
  if (!words) return null;
  // A marker as well as the colour, so the flag does not depend on seeing red.
  return <span className="pc-closed"><span aria-hidden="true">{'▲'} </span>{words}</span>;
}

/** Today's round at the building (FR-2): "Seen n of N, last seen h:mm", counts only. */
function RoundFlag({ visit }: { visit: CalendarVisit }) {
  const words = roundProgressWords(visit.round);
  if (!words) return null;
  const done = visit.round && visit.round.seen >= visit.round.total;
  return <span className={`pc-round${done ? ' done' : ''}`} aria-label={`Today's round: ${words}`}>{words}</span>;
}

function VisitChip({ visit }: { visit: CalendarVisit }) {
  const f = visit.facility;
  const stop = stopWords(visit.route_order, visit.arrival_time);
  return (
    <li className={`pc-chip${visit.building_closed ? ' closed' : ''}`}>
      {stop && <span className="pc-stop">{stop}</span>}
      <strong className="pc-fac">{f.name || 'Unnamed building'}</strong>
      <span className="pc-where">{[f.city, f.state].filter(Boolean).join(', ')}</span>
      <span className={`sc-tag pc-state ${visit.state.toLowerCase()}`}>{STATE_WORD[visit.state]}</span>
      <ClosedFlag visit={visit} />
      <RoundFlag visit={visit} />
    </li>
  );
}

export default function ProviderCalendar({ allowed = true }: { allowed?: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const week = searchParams.get('week') || '';
  const provider = searchParams.get('provider') || '';
  const [page, setPage] = useState<ProviderCalendarPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!allowed) return;
    const mine = ++seq.current;
    setLoading(true); setError(null);
    (async () => {
      try {
        const r = await getProviderCalendar({ week: week || null, provider: provider || null });
        if (mine !== seq.current) return;
        if (r.success && r.data) { setPage(r.data); setDenied(false); return; }
        // Never an empty calendar on a failure: it would read as "nobody is visiting anywhere".
        setPage(null);
        if (r.status === 403 || r.code === 'AUTHZ_DENY_NO_CAPABILITY') { setDenied(true); return; }
        setError(outcomeOf(r));
      } catch {
        if (mine === seq.current) { setPage(null); setError('Could not reach the server.'); }
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    })();
  }, [allowed, week, provider, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  const update = (patch: { week?: string | null; provider?: string | null }) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'calendar');
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    setSearchParams(next, { replace: true });
  };

  if (!allowed || denied) return <section className="um-card"><p className="um-quiet" role="note">{CALENDAR_DENIED}</p></section>;
  if (error) return <section className="um-card"><p className="um-notice stop" role="alert">{error}{' '}<button type="button" className="um-open" onClick={retry}>Try again</button></p></section>;
  if (!page) return <section className="um-card"><p className="um-quiet" role="status">Loading the provider calendar…</p></section>;

  const { week: wk, lanes, visits, upcoming, providers } = page;
  const range = `${shortDate(wk.from)} to ${shortDate(wk.through)}`;
  return (
    <section className="um-card pc" aria-busy={loading}>
      <div className="pc-controls">
        <div className="btns" role="group" aria-label="Week">
          <button type="button" className="um-open" onClick={() => update({ week: wk.prev, provider })}>Previous week</button>
          <button type="button" className="um-open" onClick={() => update({ week: null, provider })} disabled={wk.from === wk.this_week}>This week</button>
          <button type="button" className="um-open" onClick={() => update({ week: wk.next, provider })}>Next week</button>
        </div>
        <label className="pc-filter"><span>Provider</span>
          <select value={provider} onChange={(e) => update({ week: week || null, provider: e.target.value || null })} aria-label="Provider">
            <option value="">All providers</option>
            {providers.map((p) => <option key={p.id} value={p.id}>{p.name || 'Unnamed provider'}</option>)}
            <option value="none">{NO_PROVIDER}</option>
          </select>
        </label>
      </div>

      {visits.truncated && <p className="um-notice" role="note">Showing the first {visits.drawn} of {visits.total} visits this week; filter by provider.</p>}

      {lanes.length === 0
        ? <p className="sc-empty">No visits are planned this week in your buildings.</p>
        : (
          <div className="pc-scroll">
            <table className="pc-grid">
              <caption>Visits by provider, {range}</caption>
              <thead>
                <tr>
                  <th scope="col" className="pc-lane-h">Provider</th>
                  {wk.days.map((d) => (
                    <th key={d} scope="col" className={`pc-day${d === page.today ? ' today' : ''}${isWeekend(d) ? ' weekend' : ''}`} aria-current={d === page.today ? 'date' : undefined}>
                      <span className="pc-wd">{weekday(d)}</span> <span className="pc-md">{monthDay(d)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lanes.map((lane) => (
                  <tr key={lane.key}>
                    <th scope="row" className={`pc-lane${lane.provider ? '' : ' none'}`}>
                      {lane.provider ? (lane.provider.name || 'Unnamed provider') : NO_PROVIDER}
                      <span className="sc-src">{lane.visits} {lane.visits === 1 ? 'visit' : 'visits'}</span>
                    </th>
                    {wk.days.map((d) => {
                      const list = lane.days[d] || [];
                      return (
                        <td key={d} className={`pc-cell${d === page.today ? ' today' : ''}${isWeekend(d) ? ' weekend' : ''}`}>
                          {list.length === 0
                            ? <span className="pc-none"><span aria-hidden="true">{'–'}</span><span className="pc-sr">no visit</span></span>
                            : <ul className="pc-chips">{list.map((v) => <VisitChip key={`${v.plan_id}:${v.date}`} visit={v} />)}</ul>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <h2 className="pc-h">Upcoming</h2>
      {upcoming.items.length === 0
        ? <p className="sc-empty">No open visits are planned from today onward.</p>
        : (
          <div className="pc-scroll">
            <table className="sc-table pc-upcoming">
              <caption className="pc-sr">Upcoming visits, soonest first</caption>
              <thead><tr><th scope="col">Date</th><th scope="col">Building</th><th scope="col">Provider</th><th scope="col">Stop and time</th><th scope="col">State</th><th scope="col">Purpose</th></tr></thead>
              <tbody>
                {upcoming.items.map((v) => (
                  <tr key={`${v.plan_id}:${v.date}`}>
                    <td className="num">{shortDate(v.date)}<span className="sc-src">{relativeDay(v.date, page.today)}</span></td>
                    <td>{v.facility.name || 'Unnamed building'}{v.facility.city ? <span className="sc-src">{[v.facility.city, v.facility.state].filter(Boolean).join(', ')}</span> : null}<ClosedFlag visit={v} /><RoundFlag visit={v} /></td>
                    <td>{v.provider ? (v.provider.name || 'Unnamed provider') : NO_PROVIDER}</td>
                    <td>{stopWords(v.route_order, v.arrival_time) || '—'}</td>
                    <td><span className={`sc-tag pc-state ${v.state.toLowerCase()}`}>{STATE_WORD[v.state]}</span></td>
                    <td>{v.purpose || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {upcoming.total > upcoming.items.length && <p className="sc-foot">Showing {upcoming.items.length} of {upcoming.total} upcoming visits.</p>}
    </section>
  );
}
